import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BugPlay,
  Download,
  RefreshCw,
  TrendingDown,
  Loader2,
  Info,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
} from "recharts";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocalization } from "../../contexts/LocalizationContext";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

const SEVERITY_COLOR: Record<string, string> = {
  customer: "#fb923c",      // orange-400
  limits: "#a78bfa",        // violet-400
  operator: "#22d3ee",      // cyan-400
  system: "#facc15",        // yellow-400
  unknown: "#94a3b8",       // slate-400
};

const SEVERITY_LABEL_FR: Record<string, string> = {
  customer: "Cote client",
  limits: "Limites KYC",
  operator: "Operateur",
  system: "Systeme",
  unknown: "Inconnu",
};
const SEVERITY_LABEL_EN: Record<string, string> = {
  customer: "Customer-side",
  limits: "KYC limits",
  operator: "Operator",
  system: "System",
  unknown: "Unknown",
};

interface CategoryRow {
  key: string;
  fr_label: string;
  en_label: string;
  severity: string;
  hint_fr: string;
  hint_en: string;
  count: number;
  share: number;
}

interface MethodRow {
  method: string;
  count: number;
  share: number;
}

interface DayRow {
  date: string;
  count: number;
}

interface MessageRow {
  raw: string;
  method: string;
  count: number;
  last_seen: string | null;
  category_key: string;
  category_fr_label: string;
  category_en_label: string;
  severity: string;
}

interface BreakdownResponse {
  period: { days: number; from: string; to: string };
  filters: { currency: string | null; method: string | null };
  totals: { failed: number; success: number; total: number; failure_rate: number };
  by_category: CategoryRow[];
  by_method: MethodRow[];
  by_day: DayRow[];
  top_messages: MessageRow[];
}

const FailureAnalysis: React.FC = () => {
  const { t, language } = useLocalization();
  const [days, setDays] = useState<number>(7);
  const [currency, setCurrency] = useState<string>("CDF");
  const [methodFilter, setMethodFilter] = useState<string>("");
  const [data, setData] = useState<BreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const params: any = { days, currency };
      if (methodFilter) params.method = methodFilter;
      const res = await axios.get(`${API_URL}/dashboard/failures/breakdown`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [days, currency, methodFilter]);

  useEffect(() => {
    fetchBreakdown();
  }, [fetchBreakdown]);

  const fr = language === "fr";

  const labelOf = (cat: CategoryRow) => (fr ? cat.fr_label : cat.en_label);
  const sevLabel = (sev: string) =>
    fr ? SEVERITY_LABEL_FR[sev] || sev : SEVERITY_LABEL_EN[sev] || sev;

  const topCategory = data?.by_category[0] || null;
  const topMethod = data?.by_method[0] || null;

  // Bar chart data
  const chartCategoryData = useMemo(() => {
    if (!data) return [];
    return data.by_category.slice(0, 10).map((c) => ({
      name: labelOf(c),
      count: c.count,
      share: c.share,
      severity: c.severity,
      key: c.key,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, language]);

  const trendData = useMemo(() => {
    if (!data) return [];
    return data.by_day.map((d) => ({
      date: d.date.slice(5), // MM-DD
      count: d.count,
    }));
  }, [data]);

  const exportXlsx = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        data.by_category.map((c) => ({
          Category: labelOf(c),
          Severity: sevLabel(c.severity),
          Count: c.count,
          Share: `${c.share}%`,
          Hint: fr ? c.hint_fr : c.hint_en,
        })),
      ),
      "By Category",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        data.by_method.map((m) => ({
          Method: m.method,
          Count: m.count,
          Share: `${m.share}%`,
        })),
      ),
      "By Method",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        data.top_messages.map((m) => ({
          Category: fr ? m.category_fr_label : m.category_en_label,
          Method: m.method,
          Count: m.count,
          "Last Seen": m.last_seen ?? "",
          "Raw Message": m.raw,
        })),
      ),
      "Raw Messages",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(data.by_day),
      "By Day",
    );
    XLSX.writeFile(wb, `failures_${data.period.from}_to_${data.period.to}.xlsx`);
  };

  // Always expose the canonical mobile-money methods so the merchant can
  // proactively check operators where they have zero failures (good news).
  // Building this from `by_method` would hide methods that didn't fail in the
  // window, which is confusing.
  const methodOptions = useMemo(() => ["airtel", "orange", "mpesa", "afrimoney"], []);

  // ───────────────────────────────────────── UI ─────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-rose-900 shadow-xl shadow-rose-500/10 ring-1 ring-white/[0.06]">
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-rose-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 left-1/4 w-56 h-56 rounded-full bg-amber-400/12 blur-3xl pointer-events-none" />
        <div className="relative p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <BugPlay className="w-5 h-5 text-rose-300" />
            </div>
            <div>
              <div className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/25 mb-1.5">
                {fr ? "Analyse des echecs" : "Failure analysis"}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {fr ? "Pourquoi mes transactions echouent ?" : "Why are my transactions failing?"}
              </h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">
                {fr
                  ? "Categorisation automatique des messages d'erreur retournes par les operateurs sur les 7 derniers jours."
                  : "Automated categorization of operator error messages over the last 7 days."}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchBreakdown}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              {fr ? "Rafraichir" : "Refresh"}
            </button>
            <button
              onClick={exportXlsx}
              disabled={!data || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 ring-1 ring-rose-400/30 hover:bg-rose-500/30 text-rose-100 text-xs font-semibold transition-colors disabled:opacity-60"
            >
              <Download className="w-3.5 h-3.5" />
              XLSX
            </button>
          </div>
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {fr ? "Periode" : "Period"}
          </span>
          <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5">
            {[1, 3, 7].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  days === d
                    ? "bg-rose-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {fr ? "Devise" : "Currency"}
          </span>
          <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5">
            {["CDF", "USD"].map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  currency === c
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {methodOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              {fr ? "Methode" : "Method"}
            </span>
            <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5 flex-wrap">
              <button
                onClick={() => setMethodFilter("")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  methodFilter === ""
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {fr ? "Toutes" : "All"}
              </button>
              {methodOptions.map((m) => (
                <button
                  key={m}
                  onClick={() => setMethodFilter(m)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize ${
                    methodFilter === m
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="relative bg-slate-900 rounded-2xl ring-1 ring-rose-400/25 p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/15 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                {fr ? "Echecs totaux" : "Total failures"}
              </span>
              <div className="h-9 w-9 rounded-xl bg-rose-500/15 ring-1 ring-rose-400/30 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-rose-300" />
              </div>
            </div>
            <div className="text-3xl font-bold text-rose-300 tabular-nums">
              {(data?.totals.failed ?? 0).toLocaleString()}
            </div>
            <div className="text-xs text-slate-400 mt-2">
              {fr ? "sur" : "out of"} {(data?.totals.total ?? 0).toLocaleString()} tx
            </div>
          </div>
        </div>

        <div className="relative bg-slate-900 rounded-2xl ring-1 ring-amber-400/25 p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/15 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                {fr ? "Taux d'echec" : "Failure rate"}
              </span>
              <div className="h-9 w-9 rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center">
                <TrendingDown className="h-4 w-4 text-amber-300" />
              </div>
            </div>
            <div className="text-3xl font-bold text-amber-300 tabular-nums">
              {(data?.totals.failure_rate ?? 0).toFixed(2)}%
            </div>
            <div className="text-xs text-slate-400 mt-2">
              {fr ? `sur ${data?.period.days ?? 7}j en ${currency}` : `over ${data?.period.days ?? 7}d in ${currency}`}
            </div>
          </div>
        </div>

        <div className="relative bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                {fr ? "Methode #1" : "Top method"}
              </span>
            </div>
            <div className="text-xl font-bold text-white tabular-nums capitalize">
              {topMethod?.method ?? "—"}
            </div>
            <div className="text-xs text-slate-400 mt-2">
              {topMethod ? `${topMethod.count.toLocaleString()} (${topMethod.share}%)` : "—"}
            </div>
          </div>
        </div>

        <div className="relative bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                {fr ? "Categorie #1" : "Top category"}
              </span>
            </div>
            <div className="text-sm font-bold text-white">
              {topCategory ? labelOf(topCategory) : "—"}
            </div>
            <div className="text-xs text-slate-400 mt-2">
              {topCategory ? `${topCategory.count.toLocaleString()} (${topCategory.share}%)` : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Charts row: horizontal bar + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Horizontal bar */}
        <div className="lg:col-span-2 bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 shadow-lg shadow-black/20">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-white">
              {fr ? "Top 10 raisons d'echec" : "Top 10 failure reasons"}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {fr ? "Categorisation automatique" : "Automatic categorization"} · {currency}
            </p>
          </div>
          {loading ? (
            <div className="h-[360px] flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : chartCategoryData.length === 0 ? (
            <div className="h-[360px] flex items-center justify-center text-slate-500 text-sm">
              {fr ? "Aucun echec sur la periode" : "No failures on the period"}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(280, chartCategoryData.length * 38)}>
              <BarChart
                data={chartCategoryData}
                layout="vertical"
                margin={{ top: 5, right: 18, left: 16, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#cbd5e1" }}
                  width={210}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{
                    backgroundColor: "#0f172a",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: "#f1f5f9",
                  }}
                  itemStyle={{ color: "#f1f5f9" }}
                  labelStyle={{ color: "#cbd5e1", fontWeight: 600 }}
                  formatter={(value: number, _name: string, p: any) => [
                    `${value.toLocaleString()} (${p.payload.share}%)`,
                    fr ? "Echecs" : "Failures",
                  ]}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {chartCategoryData.map((entry, idx) => (
                    <Cell key={idx} fill={SEVERITY_COLOR[entry.severity] || "#94a3b8"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-white/[0.06]">
            {Object.entries(SEVERITY_COLOR).map(([sev, color]) => (
              <div key={sev} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: color }}></div>
                <span className="text-[11px] text-slate-400">{sevLabel(sev)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily trend */}
        <div className="bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 shadow-lg shadow-black/20">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-white">
              {fr ? "Tendance journaliere" : "Daily trend"}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {fr ? "Volume d'echecs par jour" : "Failure volume per day"}
            </p>
          </div>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : trendData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">
              {fr ? "Aucune donnee" : "No data"}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trendData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0f172a",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: "#f1f5f9",
                  }}
                  itemStyle={{ color: "#f1f5f9" }}
                  labelStyle={{ color: "#cbd5e1", fontWeight: 600 }}
                />
                <Line type="monotone" dataKey="count" stroke="#fb7185" strokeWidth={2.5} dot={{ r: 3, fill: "#fb7185" }} />
              </LineChart>
            </ResponsiveContainer>
          )}

          {topCategory && (
            <div className="mt-4 p-3 rounded-lg bg-rose-500/10 ring-1 ring-rose-400/25">
              <div className="flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-rose-300 flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-rose-100 leading-relaxed">
                  <span className="font-semibold">
                    {labelOf(topCategory)} :
                  </span>{" "}
                  {fr ? topCategory.hint_fr : topCategory.hint_en}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Raw messages table */}
      <div className="bg-slate-900 rounded-2xl ring-1 ring-white/[0.06] p-5 shadow-lg shadow-black/20">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">
              {fr ? "Messages bruts" : "Raw messages"}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {data?.top_messages.length ?? 0} {fr ? "messages distincts" : "distinct messages"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="h-[200px] flex items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : !data || data.top_messages.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">
            {fr ? "Aucun message" : "No message"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">{fr ? "Categorie" : "Category"}</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">{fr ? "Methode" : "Method"}</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">{fr ? "Compteur" : "Count"}</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">{fr ? "Dernier" : "Last seen"}</th>
                  <th className="px-3 py-2 font-semibold">{fr ? "Message brut" : "Raw message"}</th>
                </tr>
              </thead>
              <tbody>
                {data.top_messages.map((m, idx) => (
                  <tr key={idx} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: SEVERITY_COLOR[m.severity] || "#94a3b8" }}
                        />
                        <span className="text-slate-200 font-medium">
                          {fr ? m.category_fr_label : m.category_en_label}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-300 capitalize">{m.method}</td>
                    <td className="px-3 py-2.5 text-right text-white font-bold tabular-nums">
                      {m.count.toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-slate-400 tabular-nums whitespace-nowrap">
                      {m.last_seen ? new Date(m.last_seen).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-300 align-top">
                      <div className="whitespace-pre-wrap break-words leading-relaxed">
                        {m.raw || <em className="text-slate-500">{fr ? "(vide)" : "(empty)"}</em>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FailureAnalysis;
